import {
  type CSSProperties,
  forwardRef,
  type ReactNode,
  type UIEvent as ReactUIEvent,
} from "react";

// Default import: react-sizeme is CommonJS (`export =`), and Node can't detect `SizeMe` as a named
// export. Bundlers resolve this form too.
import sizeMe, { type SizeMeProps } from "react-sizeme";

import { cn } from "@btnet/utilities-core";

import "./Sticky.less";
import { useStickyHeight } from "./StickyContext";
import { type StickyDirection } from "./StickyContext.type";

const { SizeMe } = sizeMe;

interface IStickyProps {
  /**
   * The direction that this container will stick when scrolled to this edge of the scroll area
   * @default "top"
   */
  direction?: StickyDirection;
  /**
   * This drives the level at which the sticky container will stick in relation to other sticky containers.
   * The lower the number, the closer to the edge the container will stick.
   *
   * - | top -      0 |
   * - | top -    100 |
   * - | top -    200 |
   * - |              |
   * - | bottom - 200 |
   * - | bottom - 100 |
   * - | bottom -   0 |
   */
  level: number;
  isSticky?: boolean;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  onScroll?: (e: ReactUIEvent<HTMLDivElement, UIEvent>) => void;
  /**
   * @Default "scroll"
   */
  resizeDetectorStrategy?: "scroll" | "object";
}

const StickyInternal = forwardRef<HTMLDivElement, IStickyProps & SizeMeProps>(
  (
    {
      direction = "top",
      size,
      level,
      isSticky = true,
      className,
      style,
      onScroll,
      children,
    },
    divRef,
  ) => {
    const height = useStickyHeight(
      size.height && isSticky ? size.height : 0,
      direction,
      level,
    );
    return (
      <div
        className={cn({ Sticky__Component: isSticky }, className)}
        style={{ ...style, [direction]: height }}
        onScroll={onScroll}
        ref={divRef}
      >
        {children}
      </div>
    );
  },
);
StickyInternal.displayName = "StickyInternal";

export const Sticky = forwardRef<HTMLDivElement, IStickyProps>(
  (props, divRef) => (
    <SizeMe
      monitorHeight
      monitorWidth={false}
      noPlaceholder
      resizeDetectorStrategy={props.resizeDetectorStrategy ?? "scroll"}
    >
      {({ size }) => <StickyInternal {...props} size={size} ref={divRef} />}
    </SizeMe>
  ),
);
Sticky.displayName = "Sticky";
